import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { MemberResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { administrationApi } from '@/services/administration.api';
import { authorizationApi } from '@/services/authorization.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

export function InviteMemberDialog({
  onClose,
  onInvited,
  linkToPerson,
}: {
  onClose: () => void;
  onInvited: (member: MemberResponse) => void;
  /** Reached from a person's profile: the resulting membership is linked
   *  to this Person (see `MemberResponse.personId`), and the email field
   *  is pre-filled from their profile but stays editable — the account
   *  may need an address they haven't recorded yet. */
  linkToPerson?: { personId: string; email: string | null; displayName: string };
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [email, setEmail] = useState(linkToPerson?.email ?? '');
  const [roleKey, setRoleKey] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const rolesQuery = useQuery({
    queryKey: ['authorization', 'roles'],
    queryFn: () => authorizationApi.listRoles(),
  });
  const roles = rolesQuery.data ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      administrationApi.invite({ email, roleKey, personId: linkToPerson?.personId }),
    onSuccess: (member: MemberResponse) => onInvited(member),
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="invite-member-title"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="invite-member-title">
          {linkToPerson
            ? t('administration.member.inviteFor', { values: { name: linkToPerson.displayName } })
            : t('administration.member.invite')}
        </h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('administration.member.email')}
          name="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          dir="ltr"
          required
          autoFocus
        />

        <div className="field">
          <label className="field-label" htmlFor="member-role">
            {t('administration.member.role')}
          </label>
          <select
            id="member-role"
            className="field-input"
            value={roleKey}
            onChange={(event) => setRoleKey(event.target.value)}
          >
            <option value="" />
            {roles.map((role) => (
              <option key={role.key} value={role.key}>
                {role.name ?? t(role.nameKey, { fallback: role.key })}
              </option>
            ))}
          </select>
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!email.trim() || !roleKey}
            loading={mutation.isPending}
          >
            {linkToPerson
              ? t('administration.member.createAccount')
              : t('administration.member.invite')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
